// ** React Imports
import { createContext, useEffect, useMemo, useState } from "react";

// ** Next Import
import { useNavigate, useSearchParams } from "react-router-dom";

// ** Axios
import axios from "axios";

// ** Config
import authConfig from "../configs/auth";
import FallbackSpinner from "src/@core/components/spinner";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError } from "src/utils/utils";
import { axiosInstance } from "src/network/adapter";


// ** Defaults
const defaultProvider = {
  unReadNotificationCount: 0,
  setUnReadNotificationCount: 0,
  user: null,
  fcmToken: '',
  setFcmToken: () => null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  isInitialized: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setIsInitialized: () => Boolean,
  register: () => Promise.resolve(),
  getUnReadNotificationCount: () => Promise.resolve(),
  permissionsList: [],
  setPermissionsList: () => [],
  givenPermission: [],
  permissionsLoading: false,
  setPermissionsLoading: () => Boolean,
};
const AuthContext = createContext(defaultProvider);

const AuthProvider = ({ children }) => {
  // ** States
  const [user, setUser] = useState(defaultProvider.user);
  const [loading, setLoading] = useState(defaultProvider.loading);
  const [isInitialized, setIsInitialized] = useState(
    defaultProvider.isInitialized
  );
  const [unReadNotificationCount, setUnReadNotificationCount] = useState(defaultProvider.unReadNotificationCount)
  const [fcmToken, setFcmToken] = useState(defaultProvider.fcmToken);
  // ** Hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [permissionsList, setPermissionsList] = useState(
    defaultProvider.permissionsList
  );
  const [givenPermission, setGivenPermission] = useState(
    defaultProvider.givenPermission
  );
  const [permissionsLoading, setPermissionsLoading] = useState(
    defaultProvider.permissionsLoading
  );

  const getUnReadNotificationCount = () => {
    axiosInstance
      .get(ApiEndPoints.NOTIFICATIONS.get_unread_notifications_count)
      .then(response => response.data)
      .then(response => {
        setUnReadNotificationCount(response?.count)
      })
      .catch(error => {
      })
  }
  useEffect(() => {
    const initAuth = async () => {
      setIsInitialized(true);
      const storedToken = window.localStorage.getItem(
        authConfig.storageTokenKeyName
      );
      if (storedToken) {
        setLoading(true);
        await axios
          .get(ApiEndPoints.AUTH.me, {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          })
          .then(async (response) => {
            setLoading(false);
            const userData = response?.data?.data?.user;
            setUser(userData);
            // Extract permissions from roleInfo (only for sub-admins)
            if (userData?.user_type === "sub-admin" && userData?.roleInfo?.permissions) {
              setGivenPermission(userData.roleInfo.permissions);
            } else {
              setGivenPermission([]);
            }
          })
          .catch(() => {
            localStorage.removeItem(authConfig.storageUserDataKeyName);
            localStorage.removeItem(authConfig.storageTokenKeyName);
            setUser(null);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const handleLogin = ({ token, user }) => {
    window.localStorage.setItem(authConfig.storageTokenKeyName, token);
    setUser(user);
    // Extract permissions from roleInfo (only for sub-admins)
    if (user?.user_type === "sub-admin" && user?.roleInfo?.permissions) {
      setGivenPermission(user.roleInfo.permissions);
    } else {
      setGivenPermission([]);
    }
    const redirectUrl = searchParams.get("redirect");
    if (user?.hasWorkingHours === false) {
      navigate("/working-hours");
    } else {
      navigate(redirectUrl || "/");
    }
  };

  const handleLogout = async () => {
    try {

      await axiosInstance.post(
        ApiEndPoints.AUTH.logout)
      // Clear local storage after successful logout
      setUser(null)
      setGivenPermission([])
      window.localStorage.removeItem(authConfig.storageUserDataKeyName)
      window.localStorage.removeItem(authConfig.storageTokenKeyName)
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const response = await axios.get(ApiEndPoints.PERMISSIONS.get, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem(
            authConfig.storageTokenKeyName
          )}`,
        },
      });
      setPermissionsList(response.data.data.staff || []);
    } catch (error) {
      toastError(error);
      setPermissionsList([]); // fallback
    } finally {
      setPermissionsLoading(false);
    }
  };
  const handleRegister = () => { };

  const values = {
    user,
    loading,
    setUser,
    fcmToken,
    setFcmToken,
    setLoading,
    isInitialized,
    setIsInitialized,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister,
    permissionsList,
    setPermissionsList,
    givenPermission,
    permissionsLoading,
    setUnReadNotificationCount,
    unReadNotificationCount,
    getUnReadNotificationCount: getUnReadNotificationCount
  };

  return (
    <AuthContext.Provider value={values}>
      {loading ? <FallbackSpinner /> : children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
